import { useCallback, useContext, useMemo, useRef, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import isFunction from 'lodash/isFunction';
import queryString from 'query-string';

// GLOBAL VARIABLES
import { FILE_CHUNK_SIZE, PATH } from 'globals.js';
import { ENDPOINTS } from 'endpoints.js';

// FUNCTIONS
import { doCallback, getKey, getURL, logOut, makePath, modalFunctions, randomID, sleep } from 'functions.js';

// HELPERS
import { UploadContext } from './uploadContext';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';

// REDUX
import { store } from 'store.js';

const getFetchUrl = (endpoint, params) => {
  const url = 'https:/' + makePath(getURL(), PATH, endpoint);
  return params ? `${url}?${queryString.stringify(params)}` : url;
};

export default function useUploadFiles({
  endpoint, //
  formatParams,
  onComplete,
  onUpload: onFileUpload,
  setShowProgress,
  uploadArgs = {},
  uploadFieldValue,
  uploadMeta
}) {
  const { addUpload, removeUpload, setSuccess } = useContext(UploadContext) || {};

  const [total, setTotal] = useState(0);
  const [completed, setCompleted] = useState(0);
  const failed = useRef([]);

  // MEMOS
  const percentComplete = useMemo(() => {
    return total ? Math.ceil((completed / total) * 100) : 0;
  }, [completed, total]);

  // CALLBACKS
  const uploadFiles = useCallback(
    async (files = []) => {
      const totalUploads = files.reduce((sum, file) => (sum += Math.ceil(file.size / FILE_CHUNK_SIZE)), 0);
      let clearLoadingMessage;

      if (totalUploads > 1) {
        setShowProgress(true);
      } else {
        clearLoadingMessage = modalFunctions.loading('Uploading file');
      }

      setTotal(totalUploads);
      setCompleted(0);

      const headers = {
        Authorization: `Bearer ${store.getState().token}`,
        'x-api-key': getKey()
      };

      // Iterate files
      for (const file of files) {
        const uploadID = randomID();
        const totalChunks = Math.ceil(file.size / FILE_CHUNK_SIZE);

        headers['Content-Type'] = file.type;

        const {
          endpoint: uploadEndpoint,
          onError: onFileError,
          onFetch: onFileFetch,
          onSuccess: onFileSuccess,
          params: parameters = {}
        } = isFunction(uploadArgs) ? uploadArgs(uploadID, file) : uploadArgs;

        try {
          // Initialize
          const initiateUrl = getFetchUrl(ENDPOINTS.upload.initiate);
          const initResponse = await fetch(initiateUrl, {
            method: 'POST',
            headers: {
              ...headers,
              'Content-Length': file.size
            }
          });

          const initData = await initResponse.json();

          if (!initResponse?.ok || !initData.status) {
            const initErrorMessage = `Failed to initiate upload for ${file.name}`;
            const errorObject = {
              error: new Error(initData?.errors?.join(', ') || initData?.message || initErrorMessage),
              status: initResponse.status
            };
            throw errorObject;
          }

          const { fileChunkUUID } = initData || {};

          doCallback(addUpload, {
            uploadID,
            meta: {
              fileName: file.name
            }
          });
          doCallback(onFileFetch);

          // Upload chunks
          for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber++) {
            const start = (chunkNumber - 1) * FILE_CHUNK_SIZE;
            const end = Math.min(file.size, start + FILE_CHUNK_SIZE);
            const chunk = file.slice(start, end);
            const isLastChunk = chunkNumber === totalChunks;

            const chunkParams = {
              fileChunkUUID,
              chunkNumber,
              totalChunks
            };

            if (isLastChunk) {
              chunkParams.fileName = encodeURIComponent(file.name);
            }

            const chunkUrl = getFetchUrl(ENDPOINTS.upload.chunk, chunkParams);
            const chunkResponse = await fetch(chunkUrl, {
              method: 'POST',
              body: chunk,
              headers: {
                ...headers,
                'Content-Length': chunk.size
              }
            });

            const chunkData = await chunkResponse.json();

            const uploadErrorMessage = `Failed to upload ${file.name}`;

            if (!chunkResponse?.ok || !chunkData?.status) {
              const errorObject = {
                error: new Error(chunkData?.errors?.join(', ') || chunkData?.message || uploadErrorMessage),
                status: chunkResponse.status
              };
              throw errorObject;
            }

            setCompleted(prev => prev + 1);

            // Last chunk so update successful, call upload endpoint with fileChunkUUID and invoke callbacks
            if (isLastChunk) {
              const params = doCallback(formatParams, parameters) || parameters;
              const uploadUrl = getFetchUrl(uploadEndpoint || endpoint, { ...params, fileChunkUUID });
              const response = await fetch(uploadUrl, {
                method: 'POST',
                headers: {
                  ...headers,
                  'Content-Length': file.size
                }
              });

              const data = await response.json();

              if (!response?.ok || !data.status) {
                const errorObject = {
                  error: new Error(data?.errors?.join(', ') || data?.message || uploadErrorMessage),
                  status: response.status
                };
                throw errorObject;
              }

              const fieldValue = doCallback(uploadFieldValue, data, file);
              const meta = doCallback(uploadMeta, data, file);
              doCallback(setSuccess, uploadID, file, meta, fieldValue);
              doCallback(onFileSuccess, data);
              doCallback(onFileUpload, uploadID, file, meta, fieldValue);
            }
          }
        } catch ({ error, status }) {
          failed.current = [...failed.current, file.name];

          if (status === 401) {
            logOut({
              alerts: [
                {
                  variant: 'danger',
                  message: 'Session expired.'
                }
              ]
            }, false)
            return;
          }

          console.error(error?.message || `Failed to upload ${file.name}`);

          doCallback(removeUpload, uploadID); // Don't render uploads that failed
          doCallback(onFileError, error);
        }
      }

      // Finished uploading files so clean up, render message and invoke callback
      await sleep(1000);
      doCallback(clearLoadingMessage);
      setCompleted(0);
      setTotal(0);
      setShowProgress(false);

      const failedFiles = failed.current;

      if (failedFiles.length) {
        modalFunctions.add({
          type: 'dialog',
          title: 'Error',
          body: (
            <div className="text-start text-danger large">
              <p>Failed to upload the following files:</p>
              <ul className="ps-3">
                {failedFiles.map((fileName, i) => (
                  <li key={i} className={clsx(i !== failedFiles.length - 1 && 'mb-2')}>
                    {fileName}
                  </li>
                ))}
              </ul>
            </div>
          ),
          buttons: [<Button.Cancel label="Close" />],
          spacing: 'sm'
        });
      } else {
        modalFunctions.success('File upload successful');
      }

      doCallback(onComplete);

      failed.current = [];
    },
    [
      addUpload,
      endpoint,
      formatParams,
      onComplete,
      onFileUpload,
      removeUpload,
      setShowProgress,
      setSuccess,
      uploadArgs,
      uploadFieldValue,
      uploadMeta
    ]
  );

  return {
    percentComplete,
    uploadFiles
  };
}
